import React, { useEffect, useState } from 'react';
import { workspaceService } from '../../services/workspaceService';
import { getDirectWorkspaceAvatarSrc } from '../../utils/avatarUtils';

interface WorkspaceAvatarProps {
  workspaceId: string;
  avatar?: string;
  alt?: string;
  className: string;
  imgClassName?: string;
  fallbackStyle?: React.CSSProperties;
  children: React.ReactNode;
}

export const useWorkspaceAvatarSrc = (workspaceId?: string, avatar?: string): string | undefined => {
  const direct = getDirectWorkspaceAvatarSrc(avatar);
  const [src, setSrc] = useState<string | undefined>(direct);

  useEffect(() => {
    const nextDirect = getDirectWorkspaceAvatarSrc(avatar);
    if (nextDirect)
    {
      setSrc(nextDirect);
      return;
    }

    if (!workspaceId || !avatar || avatar.trim() === '')
    {
      setSrc(undefined);
      return;
    }

    let objectUrl: string | undefined;
    let cancelled = false;

    workspaceService
      .getAvatarBlob(workspaceId)
      .then((blob) => {
        if (cancelled)
        {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled)
        {
          setSrc(undefined);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl)
      {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [workspaceId, avatar]);

  return src;
};

export const WorkspaceAvatar: React.FC<WorkspaceAvatarProps> = ({
  workspaceId,
  avatar,
  alt = '',
  className,
  imgClassName,
  fallbackStyle,
  children,
}) => {
  const src = useWorkspaceAvatarSrc(workspaceId, avatar);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (src && !failed)
  {
    return (
      <img
        src={src}
        alt={alt}
        className={imgClassName ?? `${className} object-cover`}
        onError={() => {
          setFailed(true);
        }}
      />
    );
  }

  return (
    <div className={className} style={fallbackStyle}>
      {children}
    </div>
  );
};
